import { Grid } from '../../core/Grid.js';
import { splitEntries } from '../stateSerialization.js';
import {
    Position,
    Direction,
    positionKey,
    isInside
} from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';

export type MirrorType = '/' | '\\';

export interface LaserSegment {
    from: Position;
    to: Position;
    direction: Direction;
    teleport?: boolean;
}

export interface LaserPath {
    segments: LaserSegment[];
    digitsHit: number[];
    digitsCrossedTwice: number[];
    exited: boolean;
    exitPosition: Position | null;
}

export class MirrorsGame {
    protected grid: Grid;
    protected mirrors: Map<string, MirrorType> = new Map();
    protected currentPath: LaserPath | null = null;
    protected reversePath: LaserPath | null = null;

    protected get gs() { return this.grid.serializer; }

    constructor(grid: Grid) {
        this.grid = grid;
    }

    getGrid(): Grid {
        return this.grid;
    }

    getMirrors(): Map<string, MirrorType> {
        return new Map(this.mirrors);
    }

    isStarted(): boolean {
        return this.mirrors.size > 0;
    }

    getMirrorAt(pos: Position): MirrorType | null {
        return this.mirrors.get(positionKey(pos)) ?? null;
    }

    setMirror(pos: Position, mirror: MirrorType | null): void {
        const key = positionKey(pos);

        // Cannot place mirror on digit
        if (this.grid.hasDigitAt(pos)) return;

        if (mirror === null) {
            this.mirrors.delete(key);
        } else {
            this.mirrors.set(key, mirror);
        }

        this.currentPath = null; // Invalidate cached path
        this.reversePath = null;
    }

    cycleMirror(pos: Position): void {
        if (this.grid.hasDigitAt(pos)) return;

        const current = this.getMirrorAt(pos);
        if (current === null) {
            this.setMirror(pos, '/');
        } else if (current === '/') {
            this.setMirror(pos, '\\');
        } else {
            this.setMirror(pos, null);
        }
    }

    reset(): void {
        this.mirrors.clear();
        this.currentPath = null;
        this.reversePath = null;
    }

    /**
     * Jumpstart a solution: place mirrors so the laser reaches the first digit
     * from its entry point. The beam runs E along the (always empty) top row to
     * a "drop column", turns south, then slides along the first digit's row to
     * reach it. Tries drop columns outward from the digit and keeps the first
     * route that hits digit 1 first; prefers a route that hits *only* digit 1.
     */
    autoStart(): boolean {
        if (this.isStarted()) return false;

        const d1 = this.grid.getDigitPosition(this.grid.minDigit);
        if (!d1) return false;

        let fallback: Array<{ pos: Position; mirror: MirrorType }> | null = null;

        for (const cd of this.dropColumnOrder(d1.col)) {
            const route = this.buildDropRoute(d1, cd);
            if (!route || !this.applyMirrors(route)) continue;

            const result = this.reachesFirstDigit();
            if (!result.reaches) continue;
            if (result.clean) return true;   // beam reaches digit 1 and only digit 1
            if (!fallback) fallback = route;
        }

        if (fallback) {
            this.applyMirrors(fallback);
            return true;
        }

        this.reset();
        return false;
    }

    /** Drop columns to try: the digit's own column first, then outward. */
    private dropColumnOrder(c1: number): number[] {
        const cols: number[] = [c1];
        for (let d = 1; d < this.grid.size; d++) {
            if (c1 - d >= 0) cols.push(c1 - d);
            if (c1 + d < this.grid.size) cols.push(c1 + d);
        }
        return cols;
    }

    /**
     * Mirror placement for "drop down column cd, then slide along the digit's
     * row to it". One mirror when cd is the digit's column, two otherwise.
     * Null if the turn cell is occupied by a digit (can't host a mirror).
     */
    private buildDropRoute(
        d1: Position, cd: number
    ): Array<{ pos: Position; mirror: MirrorType }> | null {
        // Turn the beam from E (along top row) to S at the drop column.
        const route: Array<{ pos: Position; mirror: MirrorType }> = [
            { pos: { row: 0, col: cd }, mirror: '\\' },
        ];

        if (cd === d1.col) return route;  // straight down onto the digit

        const turn: Position = { row: d1.row, col: cd };
        if (this.grid.hasDigitAt(turn)) return null;
        // Slide E toward the digit (S→E is '\') or W (S→W is '/').
        route.push({ pos: turn, mirror: d1.col > cd ? '\\' : '/' });
        return route;
    }

    /** Reset, then place a candidate route's mirrors. False if any is blocked. */
    private applyMirrors(route: Array<{ pos: Position; mirror: MirrorType }>): boolean {
        this.reset();
        for (const { pos, mirror } of route) {
            this.setMirror(pos, mirror);
            if (this.getMirrorAt(pos) !== mirror) return false;
        }
        return true;
    }

    /**
     * Evaluate the placed mirrors for autoStart: whether the laser hits digit 1
     * first, and whether it hits *only* digit 1 then leaves the grid (a clean
     * "next digit 2" state rather than a stray out-of-order hit or a loop).
     */
    private reachesFirstDigit(): { reaches: boolean; clean: boolean } {
        const path = this.traceLaser();
        const min = this.grid.minDigit;
        const reaches = path.digitsHit[0] === min && !path.digitsCrossedTwice.includes(min);
        const clean = reaches &&
            path.digitsHit.length === 1 &&
            path.exited &&
            path.exitPosition === null;
        return { reaches, clean };
    }

    protected reflectDirection(dir: Direction, mirror: MirrorType): Direction {
        if (mirror === '/') {
            switch (dir) {
                case 'E': return 'N';
                case 'N': return 'E';
                case 'W': return 'S';
                case 'S': return 'W';
            }
        } else {
            switch (dir) {
                case 'E': return 'S';
                case 'S': return 'E';
                case 'W': return 'N';
                case 'N': return 'W';
            }
        }
    }

    traceLaser(): LaserPath {
        if (this.currentPath) return this.currentPath;

        const segments: LaserSegment[] = [];
        const digitsHit: number[] = [];
        const digitsCrossedTwice: number[] = [];
        const visitedCells = new Set<string>();

        // Start from top-left corner, going east
        let pos: Position = { row: 0, col: 0 };
        let dir: Direction = 'E';

        const maxIterations = this.grid.size * this.grid.size * 4; // Prevent infinite loops
        let iterations = 0;

        while (iterations < maxIterations) {
            iterations++;
            const key = positionKey(pos);

            // Check if we've visited this cell with this direction (loop detection)
            const stateKey = `${key}:${dir}`;
            if (visitedCells.has(stateKey)) {
                break; // Infinite loop
            }
            visitedCells.add(stateKey);

            // Check for digit
            const digit = this.grid.getDigitAt(pos);
            if (digit !== null) {
                if (digitsHit.includes(digit)) {
                    if (!digitsCrossedTwice.includes(digit)) {
                        digitsCrossedTwice.push(digit);
                    }
                } else {
                    digitsHit.push(digit);
                }
            }

            // Check for mirror and get new direction
            const mirror = this.getMirrorAt(pos);
            let newDir: Direction = dir;
            if (mirror) {
                newDir = this.reflectDirection(dir, mirror);
            }

            // Calculate next position
            const nextPosOrOutside = this.grid.move(pos, newDir);

            // Add segment (to position may be outside grid, but we store row/col anyway)
            segments.push({
                from: { ...pos },
                to: { row: nextPosOrOutside.row, col: nextPosOrOutside.col },
                direction: newDir
            });

            // Check if exiting grid
            if (!isInside(nextPosOrOutside)) {
                // Check if this is the correct exit (bottom-right, going east)
                const isCorrectExit =
                    pos.row === this.grid.size - 1 &&
                    pos.col === this.grid.size - 1 &&
                    newDir === 'E';

                this.currentPath = {
                    segments,
                    digitsHit,
                    digitsCrossedTwice,
                    exited: true,
                    exitPosition: isCorrectExit ? pos : null
                };
                return this.currentPath;
            }

            pos = nextPosOrOutside;
            dir = newDir;
        }

        // Didn't exit (loop or exceeded iterations)
        this.currentPath = {
            segments,
            digitsHit,
            digitsCrossedTwice,
            exited: false,
            exitPosition: null
        };
        return this.currentPath;
    }

    traceReverseLaser(): LaserPath {
        if (this.reversePath) return this.reversePath;

        const segments: LaserSegment[] = [];
        const digitsHit: number[] = [];
        const digitsCrossedTwice: number[] = [];
        const visitedCells = new Set<string>();

        // Start from bottom-right corner, going west
        let pos: Position = { row: this.grid.size - 1, col: this.grid.size - 1 };
        let dir: Direction = 'W';

        const maxIterations = this.grid.size * this.grid.size * 4;
        let iterations = 0;

        while (iterations < maxIterations) {
            iterations++;
            const key = positionKey(pos);

            const stateKey = `${key}:${dir}`;
            if (visitedCells.has(stateKey)) {
                break;
            }
            visitedCells.add(stateKey);

            const digit = this.grid.getDigitAt(pos);
            if (digit !== null) {
                if (digitsHit.includes(digit)) {
                    if (!digitsCrossedTwice.includes(digit)) {
                        digitsCrossedTwice.push(digit);
                    }
                } else {
                    digitsHit.push(digit);
                }
            }

            const mirror = this.getMirrorAt(pos);
            let newDir: Direction = dir;
            if (mirror) {
                newDir = this.reflectDirection(dir, mirror);
            }

            const nextPosOrOutside = this.grid.move(pos, newDir);

            segments.push({
                from: { ...pos },
                to: { row: nextPosOrOutside.row, col: nextPosOrOutside.col },
                direction: newDir
            });

            if (!isInside(nextPosOrOutside)) {
                const isCorrectExit =
                    pos.row === 0 &&
                    pos.col === 0 &&
                    newDir === 'W';

                this.reversePath = {
                    segments,
                    digitsHit,
                    digitsCrossedTwice,
                    exited: true,
                    exitPosition: isCorrectExit ? pos : null
                };
                return this.reversePath;
            }

            pos = nextPosOrOutside;
            dir = newDir;
        }

        this.reversePath = {
            segments,
            digitsHit,
            digitsCrossedTwice,
            exited: false,
            exitPosition: null
        };
        return this.reversePath;
    }

    /**
     * Whether the beam threads every digit in order and exits at the bottom-right —
     * the win condition apart from stray mirrors. Kept separate from checkWin so the
     * status bar can tell "solved but for unused mirrors" from "not solved yet".
     */
    private beamThreadsAllDigits(path: LaserPath): boolean {
        // Must not cross any digit twice
        if (path.digitsCrossedTwice.length > 0) return false;

        // Must exit at bottom-right
        if (!path.exitPosition) return false;
        if (path.exitPosition.row !== this.grid.size - 1 ||
            path.exitPosition.col !== this.grid.size - 1) {
            return false;
        }

        // Must hit all digits in order
        if (path.digitsHit.length !== this.grid.digitCount) return false;

        for (let i = 0; i < this.grid.digitCount; i++) {
            if (path.digitsHit[i] !== this.grid.minDigit + i) return false;
        }

        return true;
    }

    /** Mirrors no beam segment passes through — stray pieces that contribute nothing
     *  and, like Branches' dangling branches, block the win even with a valid beam. */
    getUnusedMirrors(): string[] {
        const visited = new Set<string>();
        for (const seg of this.traceLaser().segments) {
            visited.add(positionKey(seg.from));
        }
        const unused: string[] = [];
        for (const key of this.mirrors.keys()) {
            if (!visited.has(key)) unused.push(key);
        }
        return unused;
    }

    checkWin(): boolean {
        // Every placed mirror must be used: a stray mirror the beam never reaches
        // blocks the win even when the beam otherwise threads all digits and exits.
        return this.beamThreadsAllDigits(this.traceLaser()) && this.getUnusedMirrors().length === 0;
    }

    getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const path = this.traceLaser();
        const mirrorCount = this.mirrors.size;

        // Count unique empty cells visited by beam
        const visitedCells = new Set<string>();
        for (const segment of path.segments) {
            const key = positionKey(segment.from);
            if (!this.grid.hasDigitAt(segment.from)) {
                visitedCells.add(key);
            }
        }

        return calculateScore(mirrorCount, visitedCells.size);
    }

    /** Scoring-rules overlay: beam-visited empty cells (secondary). Mirrors (primary)
     *  are redrawn in amber by the renderer. */
    getScoringHighlight(): ScoringHighlight {
        const secondaryCells: Position[] = [];
        const seen = new Set<string>();
        for (const seg of this.traceLaser().segments) {
            const key = positionKey(seg.from);
            if (this.grid.hasDigitAt(seg.from) || seen.has(key)) continue;
            seen.add(key);
            secondaryCells.push({ ...seg.from });
        }
        return { secondaryCells };
    }

    getStatus(): string {
        const path = this.traceLaser();
        const boldRed = (text: string) =>
            `<span style="color:#e94560;font-weight:bold">${text}</span>`;
        const boldAmber = (text: string) =>
            `<span style="color:#fbbf24;font-weight:bold">${text}</span>`;

        if (this.checkWin()) {
            return 'You win! The beam crossed all digits in order and exited!';
        }

        // The beam threads every digit and exits, but stray mirrors the beam never
        // reaches (shown amber) keep it from being a win — flag them for removal.
        if (this.beamThreadsAllDigits(path)) {
            const unused = this.getUnusedMirrors().length;
            return `${boldAmber('Warning')}: ${unused} unused mirror${unused > 1 ? 's' : ''} — remove ${unused > 1 ? 'them' : 'it'} to win.`;
        }

        // Determine first violation by walking the path in order
        const seenDigits = new Set<number>();
        let expectedDigit = this.grid.minDigit;
        let firstViolation: 'wrong-order' | 'double-hit' | null = null;
        let wrongOrderHit = 0, wrongOrderExpected = 0;

        for (const segment of path.segments) {
            const digit = this.grid.getDigitAt(segment.from);
            if (digit === null) continue;

            if (seenDigits.has(digit)) {
                if (!firstViolation) firstViolation = 'double-hit';
            } else {
                seenDigits.add(digit);
                if (digit === expectedDigit) {
                    expectedDigit++;
                } else if (!firstViolation) {
                    firstViolation = 'wrong-order';
                    wrongOrderHit = digit;
                    wrongOrderExpected = expectedDigit;
                }
            }
        }

        // Show first violation (whichever occurred first in the path)
        if (firstViolation === 'wrong-order') {
            return `${boldRed('Wrong order')}: hit ${wrongOrderHit} but expected ${wrongOrderExpected}.`;
        }
        if (firstViolation === 'double-hit') {
            const digits = path.digitsCrossedTwice.sort((a, b) => a - b).join(', ');
            return `${boldRed('Warning')}: laser crosses digit${path.digitsCrossedTwice.length > 1 ? 's' : ''} ${digits} twice!`;
        }

        // Loop
        if (!path.exited) {
            return 'Laser is stuck in a loop.';
        }

        // All digits hit in order but wrong exit
        if (path.digitsHit.length === this.grid.digitCount) {
            return 'Place mirrors to guide beam to the bottom-right exit';
        }

        // Early exit: beam reached bottom-right exit without all digits
        if (path.exitPosition !== null) {
            return `${boldRed('Warning')}: beam must cross all digits in order before exiting`;
        }

        // Default: partial progress
        const nextDigit = this.grid.minDigit + path.digitsHit.length;
        if (nextDigit <= this.grid.maxDigit) {
            return `Place mirrors to guide beam to next digit: ${nextDigit}`;
        }

        return '';
    }

    /**
     * Serialize game state to string format.
     * Format: "A2f,B4b,C6f" - comma-separated position + type (f=/ b=\)
     */
    serialize(): string {
        const entries: string[] = [];
        for (const [key, type] of this.mirrors) {
            const [row, col] = key.split(',').map(Number);
            const posStr = this.gs.serializePosition({ row, col });
            entries.push(`${posStr}${type === '/' ? 'f' : 'b'}`);
        }
        return entries.join('');
    }

    /**
     * Deserialize game state from string format.
     */
    deserialize(serialized: string): void {
        this.reset();
        if (!serialized) return;

        for (const entry of splitEntries(serialized, 3)) {
            const posStr = entry.slice(0, 2);
            const typeChar = entry[2];

            try {
                const pos = this.gs.deserializePosition(posStr);
                const type: MirrorType = typeChar === 'f' ? '/' : '\\';
                this.setMirror(pos, type);
            } catch {
                // Invalid format, skip
            }
        }
    }
}
